import { useCallback, useEffect, useState } from 'react';
import {
  type DismissRecord,
  derivePushSetupStatus,
  type PushDriver,
  type PushLocalDiagnostics,
  shouldPromptForPush,
} from '../lib/push-setup';
import { trpc } from '../lib/trpc';

export type { PushSetupStatus } from '../lib/push-setup';

/**
 * React glue over the push policy in `lib/push-setup`. All the branching lives
 * there, pure and tested; this holds the two async reads it needs and turns the
 * result into actions.
 *
 * `driver` supplies the per-surface mechanics — `createWebPushDriver()` in a
 * portal, an adapter over `runtime.sendMessage` in the extension.
 */
export function usePushSetup(driver: PushDriver) {
  const { data: preferences } = trpc.notifications.preferences.useQuery();
  const utils = trpc.useUtils();
  const updatePreferences = trpc.notifications.updatePreferences.useMutation({
    onSuccess: (next) => utils.notifications.preferences.setData(undefined, next),
  });

  const [diagnostics, setDiagnostics] = useState<PushLocalDiagnostics | null>(null);
  const [dismissals, setDismissals] = useState<DismissRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setDiagnostics(await driver.diagnostics().catch(() => null));
  }, [driver]);

  useEffect(() => {
    void refresh();
    driver
      .readDismissals()
      .then(setDismissals)
      .catch(() => setDismissals({}));
  }, [driver, refresh]);

  const status = derivePushSetupStatus(diagnostics, preferences?.pushEnabled);

  const dismiss = useCallback(async () => {
    const next: DismissRecord = { ...(dismissals ?? {}), [status]: Date.now() };
    setDismissals(next);
    await driver.writeDismissals(next).catch(() => {});
  }, [dismissals, driver, status]);

  const enable = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      await driver.register();
      await updatePreferences.mutateAsync({ pushEnabled: true });
      // The token is written by the driver, so local state has to be re-read
      // rather than inferred from the mutation result.
      await refresh();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to turn on desktop alerts');
      return false;
    } finally {
      setBusy(false);
    }
  }, [driver, refresh, updatePreferences]);

  const disable = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      // Order matters: drop the preference first so nothing new is queued while
      // the token is being torn down.
      await updatePreferences.mutateAsync({ pushEnabled: false });
      await driver.unregister();
      await refresh();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to turn off desktop alerts');
      return false;
    } finally {
      setBusy(false);
    }
  }, [driver, refresh, updatePreferences]);

  return {
    status,
    diagnostics,
    shouldPrompt: shouldPromptForPush(status, dismissals),
    /** True for the states where a button in the UI can actually change something. */
    actionable: status === 'ready' || status === 'never-enabled' || status === 'device-missing',
    enable,
    disable,
    dismiss,
    refresh,
    busy,
    error,
  };
}
